import { useEffect } from "react"

/**
 * Find the sticky document title bar above the container and return its height.
 */
function getStickyTitleHeight(container: HTMLElement): number {
  let el: HTMLElement | null = container
  while (el) {
    const title = el.querySelector("[data-sticky-title]")
    if (title) return title.getBoundingClientRect().height
    el = el.parentElement
  }
  return 0
}

/**
 * Measures header rows in tables and sets --subheader-top on subheader rows
 * so they stick just below the header.
 */
function getStickyHeadingHeight(table: HTMLElement): number {
  // Walk up from the table to find a sticky section heading (h3)
  let el: HTMLElement | null = table.parentElement
  while (el) {
    const heading = el.querySelector(":scope > h3.sticky") as HTMLElement | null
    if (heading) return heading.getBoundingClientRect().height
    el = el.parentElement
  }
  return 0
}

function adjustSubheaderOffsets(container: HTMLElement) {
  const titleOffset = getStickyTitleHeight(container)
  const tables = container.querySelectorAll("table")
  tables.forEach((table) => {
    const sectionHeadingHeight = getStickyHeadingHeight(table as HTMLElement)
    const baseOffset = titleOffset + sectionHeadingHeight
    const rows = table.querySelectorAll("tr")
    let headerHeight = 0

    rows.forEach((row) => {
      const firstCell = row.querySelector("td, th")
      if (!firstCell) return
      const rowType = firstCell.getAttribute("data-row-type")

      if (rowType === "header") {
        headerHeight = row.getBoundingClientRect().height
        ;(row as HTMLElement).style.setProperty("top", `${baseOffset}px`)
      } else if (rowType === "subheader") {
        ;(row as HTMLElement).style.setProperty(
          "--subheader-top",
          `${baseOffset + headerHeight}px`
        )
      }
    })
  })
}

/**
 * Hook that observes a container ref and keeps subheader sticky offsets
 * in sync with header row heights.
 */
export function useStickyTableHeaders(
  ref: React.RefObject<HTMLElement | null>,
  deps: unknown[] = []
) {
  useEffect(() => {
    const el = ref.current
    if (!el) return

    adjustSubheaderOffsets(el)

    const observer = new ResizeObserver(() => adjustSubheaderOffsets(el))
    observer.observe(el)

    // Also observe each table individually so skinnier headers are caught
    const tables = el.querySelectorAll("table")
    tables.forEach((table) => observer.observe(table))

    const onResize = () => adjustSubheaderOffsets(el)
    window.addEventListener("resize", onResize)

    // Recalculate on scroll since sticky title height may shift
    const scrollParent = el.closest(".overflow-auto") ?? window
    const scrollTarget = scrollParent === window ? document : scrollParent
    scrollTarget.addEventListener("scroll", onResize, { passive: true })

    return () => {
      observer.disconnect()
      window.removeEventListener("resize", onResize)
      scrollTarget.removeEventListener("scroll", onResize)
    }
  }, [ref, ...deps])
}
